import { getNoOptionsMessage } from "helpers/DropdownHelper";
import { Dropdown, Field, Loading, OptionType } from "modules/common";
import { useGetRegistries } from "modules/common/hooks/dippHooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NotificationRecipientsFilter } from "services";
import {
    useGetRecipientsCountByCounty,
    useGetRecipientsCountByIntervention,
    useGetRecipientsCountBySettlement,
    useGetRecipientsCountByRegistry,
    useGetRecipientsStatistics,
} from "../hooks";
import { useCountiesAndSettlements } from "modules/common/hooks/useCountiesAndSettlements";

interface Props {
    userFilter?: NotificationRecipientsFilter;
    selectedUsers?: string[];
    existingNotificationId?: string;
    spanDisplay?: boolean;
}

export const NotificationStatistics = ({
    userFilter,
    selectedUsers,
    existingNotificationId,
    spanDisplay,
}: Props) => {
    const fields = spanDisplay ? 3 : undefined;
    const spanFields = spanDisplay ? 2 : undefined;

    // selected dropdown options
    const [selectedCounty, setSelectedCounty] = useState<OptionType<number | null> | null>(null);
    const [selectedSettlement, setSelectedSettlement] = useState<OptionType<number | null> | null>(
        null
    );
    const [selectedRegistry, setSelectedRegistry] = useState<number | null>();
    const [selectedIntervention, setSelectedIntervention] = useState<number | null>();

    const { countyOptions, settlementOptions, areCountiesLoading, areSettlementsLoading } =
        useCountiesAndSettlements({
            county: selectedCounty ?? undefined,
            settlement: selectedSettlement ?? undefined,
            loadCounties: true,
        });

    const { data: registries, isLoading: isLoadingRegistries } = useGetRegistries();

    const mappedRegistriesMemo = useMemo(
        () => registries?.map(c => ({ label: c.name, value: c.id })) ?? [],
        [registries]
    );

    const selectedRegistryMemo = useMemo(
        () =>
            Boolean(registries) && registries?.length !== 0
                ? registries
                      ?.filter(e => e.id === selectedRegistry)
                      .map(e => ({ label: e.name, value: e.id }))[0]
                : null,
        [registries, selectedRegistry]
    );

    // TODO: map interventions when implemented
    const mappedInterventionMemo = useMemo(
        () =>
            [] as {
                label: string;
                value: number | null;
            }[],
        []
    );

    // TODO: map when interventions are available
    const selectedInterventionMemo = useMemo(() => null, [selectedIntervention]);

    // query
    const {
        data: countByCounty,
        isFetching: isLoadingCountyCount,
        refetch: refetchCountyCount,
    } = useGetRecipientsCountByCounty(
        {
            userFilter: userFilter,
            existingNotificationId: existingNotificationId,
            recipientUids: selectedUsers,
            attributeId: selectedCounty?.value?.toString(),
        },
        Boolean(selectedCounty)
    );
    const {
        data: countBySettlement,
        isFetching: isLoadingSettlementCount,
        refetch: refetchSettlementCount,
    } = useGetRecipientsCountBySettlement(
        {
            userFilter: userFilter,
            existingNotificationId: existingNotificationId,
            recipientUids: selectedUsers,
            attributeId: selectedSettlement?.value?.toString(),
        },
        Boolean(selectedSettlement)
    );
    const {
        data: countByRegistry,
        isFetching: isLoadingRegistryCount,
        refetch: refetchRegistryCount,
    } = useGetRecipientsCountByRegistry(
        {
            userFilter: userFilter,
            recipientUids: selectedUsers,
            existingNotificationId: existingNotificationId,
            attributeId: selectedRegistry?.toString(),
        },
        Boolean(selectedRegistryMemo)
    );
    const {
        data: countByIntervention,
        isFetching: isLoadingInterventionCount,
        refetch: refetchInterventionCount,
    } = useGetRecipientsCountByIntervention(
        {
            userFilter: userFilter,
            recipientUids: selectedUsers,
            existingNotificationId: existingNotificationId,
            attributeId: selectedIntervention?.toString(),
        },
        Boolean(selectedInterventionMemo)
    );
    const { data: statistics, isFetching: isLoadingStatistics } = useGetRecipientsStatistics({
        userFilter: userFilter,
        recipientUids: selectedUsers,
        existingNotificationId: existingNotificationId,
    });

    // fetch statistics on change
    useEffect(() => {
        if (!selectedCounty) return;
        refetchCountyCount();
    }, [selectedCounty, refetchCountyCount]);

    useEffect(() => {
        if (!selectedSettlement) return;
        refetchSettlementCount();
    }, [selectedSettlement, refetchSettlementCount]);

    useEffect(() => {
        if (!selectedRegistry) return;
        refetchRegistryCount();
    }, [selectedRegistry, refetchRegistryCount]);

    useEffect(() => {
        if (!selectedIntervention) return;
        refetchInterventionCount();
    }, [selectedIntervention, refetchInterventionCount]);

    const renderStatisticsSection = useCallback(
        (statistics: number | string, component: JSX.Element) => {
            if (spanDisplay) return [statistics, component];
            else return [component];
        },
        [spanDisplay]
    );

    const county = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingCountyCount ? (
                                  <Loading size="small" />
                              ) : (
                                  countByCounty?.count ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA PO ŽUPANIJI"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {renderStatisticsSection(
                    countByCounty?.count ?? "/",
                    <Dropdown
                        placeholder="Odaberite županiju"
                        maxMenuHeight={200}
                        size="medium"
                        value={selectedCounty}
                        onChange={e => {
                            setSelectedSettlement(null);
                            setSelectedCounty(e!);
                        }}
                        options={countyOptions}
                    />
                )}
            </Field>
        ),
        [isLoadingCountyCount, countByCounty?.count, selectedCounty, countyOptions]
    );

    const municipality = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingSettlementCount ? (
                                  <Loading size="small" />
                              ) : (
                                  countBySettlement?.count ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA PO MJESTU"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {renderStatisticsSection(
                    countBySettlement?.count ?? "/",
                    <Dropdown
                        noOptionsMessage={() =>
                            getNoOptionsMessage(
                                areCountiesLoading,
                                !Boolean(selectedCounty),
                                "Odaberite županiju"
                            )
                        }
                        value={selectedSettlement}
                        placeholder="Odaberite mjesto"
                        options={settlementOptions}
                        isLoading={areSettlementsLoading}
                        onChange={e => setSelectedSettlement(e!)}
                        maxMenuHeight={200}
                        size="medium"
                    />
                )}
            </Field>
        ),
        [
            areSettlementsLoading,
            isLoadingSettlementCount,
            settlementOptions,
            selectedSettlement,
            countBySettlement?.count,
            areCountiesLoading,
            selectedCounty,
        ]
    );

    const noEmail = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingStatistics ? (
                                  <Loading size="small" />
                              ) : (
                                  statistics?.noEmailAddressCount ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA BEZ EMAIL ADRESE"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {spanDisplay && (statistics?.noEmailAddressCount ?? "/")}
            </Field>
        ),
        [isLoadingStatistics, statistics?.noEmailAddressCount]
    );

    const notRegistered = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingStatistics ? (
                                  <Loading size="small" />
                              ) : (
                                  statistics?.unregisteredCount ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ NEREGISTRIRANIH PRIMATELJA"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {spanDisplay && (statistics?.unregisteredCount ?? "/")}
            </Field>
        ),
        [isLoadingStatistics, statistics?.unregisteredCount]
    );

    const emailRecipients = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingStatistics ? (
                                  <Loading size="small" />
                              ) : (
                                  statistics?.recipientsByEmailCount ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA EMAILOM"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {spanDisplay && (statistics?.recipientsByEmailCount ?? "/")}
            </Field>
        ),
        [isLoadingStatistics, statistics?.recipientsByEmailCount]
    );

    const appRecipients = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingStatistics ? (
                                  <Loading size="small" />
                              ) : (
                                  statistics?.recipientsByAppCount ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA APLIKACIJOM"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {spanDisplay && (statistics?.recipientsByAppCount ?? "/")}
            </Field>
        ),
        [isLoadingStatistics, statistics?.recipientsByAppCount]
    );

    const registry = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingRegistryCount ? (
                                  <Loading size="small" />
                              ) : (
                                  countByRegistry?.count ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA PO REGISTRIMA"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {renderStatisticsSection(
                    countByRegistry?.count ?? "/",
                    <Dropdown
                        isLoading={isLoadingRegistries}
                        noOptionsMessage={() =>
                            getNoOptionsMessage(
                                isLoadingRegistries,
                                Boolean(selectedIntervention),
                                "Odaberite registar"
                            )
                        }
                        value={selectedRegistryMemo}
                        options={mappedRegistriesMemo}
                        onChange={e => setSelectedRegistry(e?.value)}
                        placeholder="Odaberite registar"
                        maxMenuHeight={200}
                        size="medium"
                    />
                )}
            </Field>
        ),
        [
            countByRegistry?.count,
            isLoadingRegistries,
            isLoadingRegistryCount,
            mappedRegistriesMemo,
            selectedIntervention,
            selectedRegistryMemo,
        ]
    );

    const intervention = useMemo(
        () => (
            <Field
                inlineRemarks={
                    spanDisplay
                        ? []
                        : [
                              isLoadingInterventionCount ? (
                                  <Loading size="small" />
                              ) : (
                                  countByIntervention?.count ?? "/"
                              ),
                          ]
                }
                ptClassName={{ inlineRemarks: ["f-a-x-c"] }}
                horizontal
                name="BROJ PRIMATELJA PO INTERVENCIJAMA"
                fieldColumns={fields}
                spanFieldColumn={spanFields}
            >
                {renderStatisticsSection(
                    countByIntervention?.count ?? "/",
                    <Dropdown
                        isLoading={isLoadingInterventionCount}
                        noOptionsMessage={() =>
                            getNoOptionsMessage(
                                false, // TODO: this should be replaced when interventions are implemented
                                Boolean(selectedIntervention),
                                "Odaberite intervenciju"
                            )
                        }
                        value={selectedInterventionMemo}
                        options={mappedInterventionMemo}
                        onChange={e => setSelectedIntervention(e?.value)}
                        placeholder="Odaberite intervenciju"
                        maxMenuHeight={200}
                        size="medium"
                    />
                )}
            </Field>
        ),
        [
            countByIntervention?.count,
            isLoadingInterventionCount,
            mappedInterventionMemo,
            selectedIntervention,
            selectedInterventionMemo,
        ]
    );

    return (
        <div className="notification-statistics">
            {county}
            {municipality}
            {noEmail}
            {notRegistered}
            {emailRecipients}
            {appRecipients}
            {registry}
            {intervention}
        </div>
    );
};
